/**
 * Unified sign-out across the Scalens apps (scalens-architecture auth/unified-signout.md).
 * Signing out writes the time into a cookie that every *.scalens.com app on the same
 * Auth0 tenant reads; an app whose login started before that time signs out too.
 * Same rules as Chat's frontend/lib/auth/signout-sync.ts (tested there).
 */

const NOTE_MAX_AGE = 30 * 24 * 60 * 60; // longest login of any app: 30-day refresh tokens
// The apps' and Auth0's clocks differ by far less than this. A login that starts this
// close before a sign-out elsewhere is kept, so a fresh login is never signed out.
const CLOCK_MARGIN = 5;
// A note further in the future than this is ignored (a forged cookie). Wide, because
// this browser's clock may be off; the same limit in every app.
const MAX_FUTURE = 24 * 60 * 60;
const SERVER_TIME_TIMEOUT_MS = 2000;
// The sign-out this tab followed last, so a clock difference can never cause a loop.
const SEEN = 'scalens_signout_seen';

export const SIGNOUT_CHECK_EVERY_MS = 5000;

// When this page's login started. Investigate keeps its login in memory, so every page
// load logs in again through Auth0: the issue time of the page's first ID token.
let loginAt = 0;

const now = () => Math.floor(Date.now() / 1000);

function noteName() {
  const tenant = process.env.REACT_APP_TESTING_DOMAINE;
  if (process.env.REACT_APP_SIGNOUT_SYNC === 'off' || !tenant) return null;
  if (!window.location.hostname.endsWith('.scalens.com')) return null;
  // One note per Auth0 tenant, so the dev and legacy tenants never sign out production.
  const host = tenant
    .replace(/^https?:\/\//, '')
    .split('/')[0]
    .toLowerCase();
  return `scalens_signout_${host.replace(/[^a-z0-9]/g, '_')}`;
}

function readNote(name) {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  const value = Number(match ? match[1] : '');
  return Number.isInteger(value) && value <= now() + MAX_FUTURE ? value : 0;
}

// The time on our own server (Date, plus Age if a cache answered): this browser's clock
// may be off. Gives up after 2 s so signing out is never held up.
async function serverNow() {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), SERVER_TIME_TIMEOUT_MS);
  try {
    const response = await fetch(`/?signout=${Date.now()}`, {
      method: 'HEAD',
      cache: 'no-store',
      signal: controller.signal,
    });
    const date = Date.parse(response.headers.get('date') || '');
    const age = Number(response.headers.get('age')) || 0;
    if (!Number.isNaN(date)) return Math.floor(date / 1000) + age;
  } catch {
    // offline or slow: fall back to this browser's clock
  } finally {
    clearTimeout(timer);
  }
  return now();
}

/** Before signing out: write the sign-out time into the note. */
export async function noteSignout() {
  loginAt = 0;
  const name = noteName();
  if (!name) return;
  const time = await serverNow();
  // Always a new value, so every open app notices, even after a note from a fast clock.
  const latest = readNote(name);
  const signedOutAt = latest >= time ? latest + 1 : time;
  document.cookie = `${name}=${signedOutAt}; Domain=scalens.com; Path=/; Max-Age=${NOTE_MAX_AGE}; Secure; SameSite=Lax`;
}

/** Remember when this page's login started (first call wins). */
export function noteLogin(issuedAt) {
  if (!loginAt && issuedAt) loginAt = issuedAt;
}

/** True when another app signed out after this login started: the caller signs out here. */
export function signedOutElsewhere() {
  const name = noteName();
  if (!name || !loginAt) return false;
  const signedOutAt = readNote(name);
  if (loginAt >= signedOutAt - CLOCK_MARGIN) return false;
  // This tab followed this sign-out already. A newer login that still looks older than
  // it can only come from a clock difference: keep it rather than loop.
  if (sessionStorage.getItem(SEEN) === String(signedOutAt)) return false;
  sessionStorage.setItem(SEEN, String(signedOutAt));
  loginAt = 0;
  return true;
}
